/**
 * Step Indicator Component
 *
 * Shows the wizard progress with step dots and a progress bar.
 */

import type { WizardStepInfo } from '../../services/setupService';

interface StepIndicatorProps {
  steps: WizardStepInfo[];
  currentStepIndex: number;
  progressPercent: number;
}

export function StepIndicator({ steps, currentStepIndex, progressPercent }: StepIndicatorProps) {
  return (
    <div className="space-y-2">
      {/* Progress bar */}
      <div className="h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary-500 transition-all duration-300 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Step dots */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const isUpcoming = index > currentStepIndex;

          return (
            <div key={step.id} className="flex flex-col items-center">
              {/* Dot */}
              <div
                className={`
                  w-3 h-3 rounded-full transition-all duration-200
                  ${isCompleted ? 'bg-primary-500' : ''}
                  ${isCurrent ? 'bg-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' : ''}
                  ${isUpcoming ? 'bg-secondary-300 dark:bg-secondary-600' : ''}
                `}
              />

              {/* Label (hidden on mobile, shown on larger screens) */}
              <span
                className={`
                  hidden sm:block mt-1 text-xs transition-colors duration-200
                  ${isCurrent ? 'text-primary-600 dark:text-primary-400 font-medium' : ''}
                  ${isCompleted ? 'text-secondary-600 dark:text-secondary-400' : ''}
                  ${isUpcoming ? 'text-secondary-400 dark:text-secondary-500' : ''}
                `}
              >
                {step.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default StepIndicator;
